import * as React from "react"
import classnames from "classnames"
import "./typography.scss"
import { Color } from "types/bootstrap"

type Variant =
	| "h1"
	| "h2"
	| "h3"
	| "h4"
	| "h5"
	| "h6"
	| "subtitle1"
	| "subtitle2"
	| "body1"
	| "body2"
	| "caption"
	| "button"
	| "overline"
	| "srOnly"
	| "inherit"
type Alignment = "inherit" | "left" | "center" | "right" | "justify"
type Transform = "initial" | "lowercase" | "uppercase" | "capitalize"
type Weight = "initial" | "bold" | "bolder" | "normal" | "light" | "lighter"
type Component =
	| "p"
	| "span"
	| "h1"
	| "h2"
	| "h3"
	| "h4"
	| "h5"
	| "h6"
	| "button"
type Direction = "ltr" | "rtl" | "initial"

const defaultVariantMapping = {
	h1: "h1",
	h2: "h2",
	h3: "h3",
	h4: "h4",
	h5: "h5",
	h6: "h6",
	subtitle1: "h6",
	subtitle2: "h6",
	body1: "p",
	body2: "p",
}

interface Typography {
	align?: Alignment
	color?: Color
	component?: Component
	gutterBottom?: boolean
	noWrap?: boolean
	paragraph?: boolean
	truncate?: boolean
	bold?: boolean
	italic?: boolean
	weight?: Weight
	transform?: Transform
	variant?: Variant
	direction?: Direction
	className?: string
}

const Typography: React.FC<
	Typography & React.HTMLProps<HTMLParagraphElement>
> = ({
	align = "inherit",
	color = "initial",
	component,
	transform = "initial",
	gutterBottom = false,
	noWrap = false,
	paragraph = false,
	truncate = false,
	bold = false,
	italic = false,
	direction = "initial",
	weight = "initial",
	variant = "initial",
	className = "",
	...other
}) => {
	const Component =
		component ||
		(paragraph ? "p" : defaultVariantMapping[variant]) ||
		"span"
	return (
		<Component
			className={classnames(
				"m-0",
				noWrap ? "text-nowrap" : "text-wrap",
				{ ["font-weight-bold"]: bold },
				{ ["mb-2"]: gutterBottom },
				{ ["font-italic"]: italic },
				{ ["text-truncate"]: truncate },
				{ [`text-${variant}`]: variant !== "initial" },
				{
					[`text-${align}`]: align !== "inherit",
				},
				{
					[`text-${transform}`]: transform !== "initial",
				},
				{
					[`font-weight-${weight}`]: weight !== "initial",
				},
				{
					[`text-${direction}`]: direction !== "initial",
				},
				{
					[`text-${color}`]: color !== "initial",
				},
				className
			)}
			{...other}
		/>
	)
}

export default Typography
